import { Buffer } from "buffer";
import { Helper } from "../helpers/helper";
import { DocumentItem, ImisPayloadFieldType, ImisResponseItems, ImisResponseResult } from "../models/imis.models";
import { PayloadUtility } from "./payload.utility";
import { RestUtility } from "./rest.utility";

/**
 * Static helpers for retrieving iMIS documents (by type, name, path, folder, version ID, or ID for their content) and for creating or updating documents via the REST API. Lookup methods throw when no response is received and return null/empty results when nothing matches.
 */
export class DocumentsUtility {

    /**
     * Retrieves all document summaries of a given document type, optionally restricted to system documents and/or published documents only.
     * @param documentType The document type ID to filter by.
     * @param isSystem Whether to filter for system documents.
     * @param isPublishedOnly Whether to restrict results to published documents (status 40) only; when false, no document status filter is applied.
     * @returns All matching document items, ordered by name ascending.
     */
    public static getDocumentsByType(documentType: string, isSystem: boolean, isPublishedOnly: boolean): Promise<ImisResponseItems<DocumentItem>> {
        return RestUtility.restApiGetDataAll<DocumentItem>("/api/documentsummary", {
            documenttypeid: documentType,
            isSystem,
            documentstatusid: isPublishedOnly ? 40 : null,
            orderby: "Name:ascending"
        });
    }

    /**
     * Finds the document at the given path and returns its latest version (most recently updated, which may be an unpublished draft). The path lookup resolves to the latest published document, whose version ID is then used to get the latest version. If the path lookup is unsuccessful (e.g. the document has never been published), falls back to searching by the document's name and matching on its path, returning the most recently updated match.
     * @param path The full path of the document (folder path plus name); the name is taken as the text after the last slash.
     * @returns The latest document item, or null if the document cannot be found by path or by the name fallback.
     * @throws {Error} If no response is received from the path lookup request.
     */
    public static async getDocumentLatestVersionByPath(path: string): Promise<DocumentItem | null> {
        // getting document by path will return the latest published document on that path
        // use that document's version id to get the latest document (sorted by update date)
        let documentByPathResponse = await RestUtility.restApiSendData<ImisResponseResult<DocumentItem>>("/api/documentsummary/_execute", {
            "$type": "Asi.Soa.Core.DataContracts.GenericExecuteRequest, Asi.Contracts",
            "OperationName": "FindByPath",
            "EntityTypeName": "DocumentSummary",
            "Parameters": {
                "$type": "System.Collections.ObjectModel.Collection`1[[System.Object, mscorlib]], mscorlib",
                "$values": [
                    {
                        "$type": "System.String",
                        "$value": path
                    }
                ]
            },
            "UseJson": false
        });
        if (documentByPathResponse == null) {
            throw new Error("Failed to get document by path: " + path);
        }
        if (!documentByPathResponse.IsSuccessStatusCode) {
            // path lookup failed because document does not exist or is unpublished
            // try retrieving document using the document's name and filter by the path
            let documentName = path.substring(path.lastIndexOf("/") + 1);
            let documentsByName = await this.getDocumentsByName(documentName);
            let documentByPath = documentsByName.find(d => d.Path == path);
            if (documentByPath == null) {
                return null;
            }

            // the document we got is most likely the latest document (sorted by update date), so return it
            return documentByPath;
        }
        
        let documentByPath = documentByPathResponse.Result;
        return this.getDocumentLatestVersionByVersionId(documentByPath.DocumentVersionId);
    }

    /**
     * Retrieves the most recently updated document summary matching the given document version ID.
     * @param documentVersionId The document version ID to look up.
     * @returns The most recently updated matching document item, or null if no document matches.
     * @throws {Error} If no response is received from the document summary request.
     */
    public static async getDocumentLatestVersionByVersionId(documentVersionId: string): Promise<DocumentItem | null> {
        let documentItem: DocumentItem;

        let documentItemResponse = await RestUtility.restApiGetData<ImisResponseItems<DocumentItem>>("/api/documentsummary", {
            DocumentVersionId: documentVersionId,
            orderby: "UpdatedOn:descending",
            limit: 1,
        });
        if (documentItemResponse == null) {
            throw new Error("Failed to get document by version id: " + documentVersionId);
        }
        if (documentItemResponse.Items.length == 0) {
            return null;
        }
        documentItem = documentItemResponse.Items[0];

        return documentItem;
    }

    /**
     * Retrieves all document summaries with the given name, regardless of folder or publish status.
     * @param name The document name to filter by.
     * @returns All matching document items, ordered by most recently updated first.
     * @throws {Error} If no response is received from the document summary request.
     */
    public static async getDocumentsByName(name: string): Promise<DocumentItem[]> {
        let documentsByNameResponse = await RestUtility.restApiGetDataAll<DocumentItem>("/api/documentsummary", {
            name,
            orderby: "UpdatedOn:descending",
        });
        if (documentsByNameResponse == null) {
            throw new Error("Failed to get documents by name: " + name);
        }

        return documentsByNameResponse.Items;
    }

    /**
     * Finds all documents in the given folder via the DocumentSummary `FindDocumentsInFolder` operation.
     * @param pathToFolder The folder path to search; a trailing slash is appended if missing.
     * @param filterDocumentTypeIds Document type IDs to restrict results to; when empty, no type filter is applied.
     * @param publishedOnly Whether to restrict results to published documents.
     * @returns The matching document items, or an empty array if the request returns an unsuccessful status.
     * @throws {Error} If no response is received from the folder lookup request.
     */
    public static async findDocumentsInFolderByPath(pathToFolder: string, filterDocumentTypeIds: string[] = [], publishedOnly: boolean = false): Promise<DocumentItem[]> {
        // ensure path to folder ends with a slash
        if (!pathToFolder.endsWith("/")) {
            pathToFolder += "/";
        }

        // find all documents in the folder
        let findDocumentsInFolderByPathResponse = await RestUtility.restApiSendData<ImisResponseResult<DocumentItem[]>>("/api/documentsummary/_execute", {
            "$type": "Asi.Soa.Core.DataContracts.GenericExecuteRequest, Asi.Contracts",
            "OperationName": "FindDocumentsInFolder",
            "EntityTypeName": "DocumentSummary",
            "Parameters": {
                "$type": "System.Collections.ObjectModel.Collection`1[[System.Object, mscorlib]], mscorlib",
                "$values": [
                    {
                        "$type": "System.String",
                        "$value": pathToFolder
                    },
                    {
                        "$type": "System.String[]",
                        "$values": filterDocumentTypeIds
                    },
                    {
                        "$type": "System.Boolean",
                        "$value": publishedOnly
                    }
                ]
            }
        });
        if (findDocumentsInFolderByPathResponse == null) {
            throw new Error("Failed to find documents in folder by path: " + pathToFolder);
        }
        if (!findDocumentsInFolderByPathResponse.IsSuccessStatusCode) {
            return [];
        }

        return findDocumentsInFolderByPathResponse.Result;
    }

    /**
     * Retrieves a document's content by its document ID and decodes it from its base64 binary payload into a string.
     * @param documentId The ID of the document whose content should be retrieved.
     * @returns The decoded document content, or an empty string if no document is found for the ID.
     * @throws {Error} If no response is received from the document request.
     */
    public static async getDocumentDataById(documentId: string): Promise<string> {
        let documentDataResponse = await RestUtility.restApiGetData<ImisResponseItems<DocumentItem>>("/api/document", {
            documentId
        });
        if (documentDataResponse == null) {
            throw new Error("Failed to get document data by id: " + documentId);
        }
        if (documentDataResponse.Items.length == 0) {
            return "";
        }

        let documentItem = documentDataResponse.Items[0];
        return Buffer.from(documentItem.Data.$value, "base64").toString();
    }

    /**
     * Creates a new document at the given folder path, or updates the existing document in place if one with the same name already exists there. New documents are created with a status of "Published"; updates send only the name, document type and (if provided) data, so the path and status of an existing document are left unchanged.
     * @param fileName The name of the document/file.
     * @param folderPath The folder path the document lives (or will be created) in; trailing slashes are stripped before use.
     * @param fileType The document type ID to assign when creating a new document.
     * @param data The document's content as a string, encoded into the binary payload field; when empty/null, the Data field is omitted from the request.
     * @returns The REST API response from creating or updating the document.
     */
    public static async addUpdateDocument(fileName: string, folderPath: string, fileType: string, data: string) {
        let addUpdateDocumentResponse = null;
        let dataPayloadFieldValue = PayloadUtility.createImisPayloadNameValuePair("Data", data, { Data: ImisPayloadFieldType.BINARY }).Value;

        // remove any trailing slashes
        let cleanFolderPath = folderPath.replace(/\/+$/g, "");
        let response: ImisResponseItems<DocumentItem> = await RestUtility.restApiGetData("/api/documentsummary", { path: [cleanFolderPath, fileName].join("/") });
        if (response == null || response.Items.length == 0) {
            let payload = {
                Name: fileName,
                Path: cleanFolderPath,
                Status: "Published",
                DocumentTypeId: fileType,
                Data: null
            };
            if (!Helper.isStringNullOrEmpty(data)) {
                payload.Data = dataPayloadFieldValue;
            }

            addUpdateDocumentResponse = await RestUtility.restApiSendData("/api/document", payload);
        } else {
            // update the existing document
            let documentId = response.Items[0].DocumentId;

            let payload = {
                DocumentId: documentId,
                Name: fileName,
                DocumentTypeId: fileType,
                Data: null
            };
            if (!Helper.isStringNullOrEmpty(data)) {
                payload.Data = dataPayloadFieldValue;
            }

            addUpdateDocumentResponse = await RestUtility.restApiSendData(`/api/document/${documentId}`, payload, "PUT");
        }

        return addUpdateDocumentResponse;
    }

}